import { useState, useEffect } from "react";

/**
 * Custom hook to monitor internet connection strength and status
 * @returns {Object} Network status information including:
 *   - isOnline: boolean - Whether device is online
 *   - connectionStrength: string - 'excellent' | 'good' | 'fair' | 'poor' | 'offline'
 *   - effectiveType: string - Network effective type (e.g., '4g', '3g', 'slow-2g')
 *   - downlink: number - Estimated downlink speed in Mbps
 *   - rtt: number - Round-trip time in milliseconds
 *   - saveData: boolean - Whether save-data mode is enabled
 *   - connectionType: string - Connection type (e.g., 'wifi', 'cellular', 'ethernet')
 */
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState({
    isOnline: navigator.onLine,
    connectionStrength: navigator.onLine ? "good" : "offline",
    effectiveType: null,
    downlink: null,
    rtt: null,
    saveData: false,
    connectionType: null,
  });

  useEffect(() => {
    // Function to get connection strength based on network information
    const getConnectionStrength = (connection) => {
      if (!navigator.onLine || !connection) {
        return "offline";
      }

      const { effectiveType, downlink, type } = connection;
      const connectionType = type || (effectiveType?.includes("g") ? "cellular" : null);
      console.log("connectionType", connectionType,effectiveType);
      // Landline/Ethernet connections (typically excellent)
      if (connectionType === "ethernet" || connectionType === "wired") {
        if (downlink && downlink >= 50) {
          return "excellent";
        } else if (downlink && downlink >= 10) {
          return "good";
        } else if (downlink && downlink >= 5) {
          return "fair";
        }
        return "good"; // Default for ethernet even without speed data
      }

      // 5G Network handling
      if (effectiveType === "5g") {
        if (downlink && downlink >= 50) {
          return "excellent";
        } else if (downlink && downlink >= 20) {
          return "excellent";
        } else if (downlink && downlink >= 10) {
          return "good";
        } else if (downlink && downlink >= 2) {
          return "good";
        } else {
          return "fair"; // 5G with low speed still better than 4G
        }
      }

      // 4G Network handling
      if (effectiveType === "4g") {
        if (downlink && downlink >= 20) {
          return "excellent";
        } else if (downlink && downlink >= 10) {
          return "good";
        } else if (downlink && downlink >= 2) {
          return "good";
        } else if (downlink && downlink >= 1) {
          return "fair";
        } else {
          return "fair"; // 4G with unknown speed
        }
      }

      // 3G Network handling
      if (effectiveType === "3g") {
        if (downlink && downlink >= 5) {
          return "good";
        } else if (downlink && downlink >= 1) {
          return "fair";
        } else {
          return "poor";
        }
      }

      // 2G Network handling
      if (effectiveType === "2g" || effectiveType === "slow-2g") {
        return "poor";
      }

      // WiFi connections (when effectiveType is not available but connection type is wifi)
      if (connectionType === "wifi") {
        if (downlink && downlink >= 50) {
          return "excellent";
        } else if (downlink && downlink >= 20) {
          return "excellent";
        } else if (downlink && downlink >= 10) {
          return "good";
        } else if (downlink && downlink >= 5) {
          return "good";
        } else if (downlink && downlink >= 1) {
          return "fair";
        } else {
          return "good"; // WiFi default assumption
        }
      }

      // Fallback: Determine by downlink speed only
      if (downlink) {
        if (downlink >= 50) {
          return "excellent";
        } else if (downlink >= 20) {
          return "excellent";
        } else if (downlink >= 10) {
          return "good";
        } else if (downlink >= 5) {
          return "good";
        } else if (downlink >= 1) {
          return "fair";
        } else {
          return "poor";
        }
      }

      // Default fallback
      return "fair";
    };

    // Function to get connection type
    const getConnectionType = (connection) => {
      if (!connection) return null;
      
      // Check if connection.type is available (Chrome/Edge)
      // Possible values: 'bluetooth', 'cellular', 'ethernet', 'none', 'wifi', 'wimax', 'other', 'unknown'
      if (connection.type) {
        // Map ethernet to landline for better understanding
        if (connection.type === "ethernet") {
          return "landline";
        }
        return connection.type;
      }
      
      // Fallback: infer from effectiveType
      if (connection.effectiveType) {
        if (connection.effectiveType.includes("5g") || 
            connection.effectiveType.includes("4g") || 
            connection.effectiveType.includes("3g") || 
            connection.effectiveType.includes("2g")) {
          return "cellular";
        }
      }
      
      // If we have high downlink but no type, likely ethernet/landline
      if (connection.downlink && connection.downlink >= 50) {
        return "landline";
      }
      
      return "unknown";
    };

    // Function to update network status
    const updateNetworkStatus = () => {
      const isOnline = navigator.onLine;
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      if (connection) {
        setNetworkStatus({
          isOnline,
          connectionStrength: getConnectionStrength(connection),
          effectiveType: connection.effectiveType || null,
          downlink: connection.downlink || null,
          rtt: connection.rtt || null,
          saveData: connection.saveData || false,
          connectionType: getConnectionType(connection),
        });
      } else {
        // Fallback when Network Information API is not available
        setNetworkStatus({
          isOnline,
          connectionStrength: isOnline ? "good" : "offline",
          effectiveType: null,
          downlink: null,
          rtt: null,
          saveData: false,
          connectionType: null,
        });
      }
    };

    // Initial update
    updateNetworkStatus();

    // Event listeners for online/offline status
    const handleOnline = () => updateNetworkStatus();
    const handleOffline = () => updateNetworkStatus();

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Event listener for connection changes (Network Information API)
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener("change", updateNetworkStatus);
    }

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (connection) {
        connection.removeEventListener("change", updateNetworkStatus);
      }
    };
  }, []);

  return networkStatus;
};

